/**
 * Floral SVG ornaments — thin gold line-art botanicals.
 * Use `color` (currentColor) on the parent to recolor.
 */

const STROKE = 1.2;

/** Horizontal centerpiece divider with central bloom + leafy curling stems. */
export const FloralDivider = ({ className = "", width = 320, ...rest }) => (
  <svg
    width={width}
    height={(width * 36) / 320}
    viewBox="0 0 320 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    {...rest}
  >
    {/* Left stem */}
    <path
      d="M16 18 Q 60 6, 100 18 T 148 18"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      fill="none"
    />
    {/* Left leaves */}
    <path d="M70 13 q 6 -8 14 -6 q -2 8 -14 6 Z" fill="currentColor" opacity="0.85" />
    <path d="M115 22 q 5 7 13 6 q 0 -7 -13 -6 Z" fill="currentColor" opacity="0.85" />
    <circle cx="40" cy="14" r="1.6" fill="currentColor" opacity="0.7" />

    {/* Central bloom (5-petal rosette) */}
    <g transform="translate(160 18)">
      <circle r="3.2" fill="currentColor" />
      {[0, 72, 144, 216, 288].map((d) => (
        <ellipse
          key={d}
          cx="0"
          cy="-6.5"
          rx="2.4"
          ry="4.5"
          fill="currentColor"
          opacity="0.9"
          transform={`rotate(${d})`}
        />
      ))}
      <circle r="1.4" fill="#0F2447" />
    </g>

    {/* Right stem (mirrored) */}
    <path
      d="M172 18 Q 220 30, 260 18 T 304 18"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      fill="none"
    />
    <path d="M236 22 q 6 8 14 6 q -2 -8 -14 -6 Z" fill="currentColor" opacity="0.85" />
    <path d="M205 14 q 5 -7 13 -6 q 0 7 -13 6 Z" fill="currentColor" opacity="0.85" />
    <circle cx="280" cy="22" r="1.6" fill="currentColor" opacity="0.7" />
  </svg>
);

/** Small downward-facing sprig — perfect above an eyebrow/heading. */
export const FloralSprig = ({ className = "", width = 70, ...rest }) => (
  <svg
    width={width}
    height={(width * 50) / 70}
    viewBox="0 0 70 50"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    {...rest}
  >
    {/* central stem */}
    <path
      d="M35 4 C 35 18, 35 32, 35 46"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
    />
    {/* paired leaves */}
    <path
      d="M35 16 q -12 -2 -16 -10 q 12 -2 16 10 Z"
      fill="currentColor"
      opacity="0.85"
    />
    <path
      d="M35 16 q 12 -2 16 -10 q -12 -2 -16 10 Z"
      fill="currentColor"
      opacity="0.85"
    />
    <path
      d="M35 30 q -10 0 -13 -7 q 10 -2 13 7 Z"
      fill="currentColor"
      opacity="0.7"
    />
    <path
      d="M35 30 q 10 0 13 -7 q -10 -2 -13 7 Z"
      fill="currentColor"
      opacity="0.7"
    />
    {/* tiny bud at the end */}
    <circle cx="35" cy="46" r="2.2" fill="currentColor" />
  </svg>
);

/** Refined corner ornament — a graceful arching stem from the corner with a few well-spaced leaves and a single bloom. */
export const FloralCorner = ({ className = "", width = 180, ...rest }) => {
  // Reusable leaf glyph centered at (0,0), pointing up
  const Leaf = ({ x, y, rot, size = 1, stroke = 1.1, opacity = 1 }) => {
    const w = 7 * size;
    const h = 18 * size;
    return (
      <g transform={`translate(${x} ${y}) rotate(${rot})`} opacity={opacity}>
        <path
          d={`M0 ${-h} C ${w} ${-h * 0.45}, ${w} ${h * 0.45}, 0 ${h} C ${-w} ${h * 0.45}, ${-w} ${-h * 0.45}, 0 ${-h} Z`}
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
        />
        <line
          x1="0"
          y1={-h + 2}
          x2="0"
          y2={h - 2}
          stroke="currentColor"
          strokeWidth={stroke * 0.55}
          opacity="0.7"
        />
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={width}
      viewBox="0 0 180 180"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      {...rest}
    >
      {/* Main graceful stem — gentle S-curve from corner outward */}
      <path
        d="M6 6 C 40 28, 70 60, 96 96 S 138 150, 160 168"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />

      {/* Three large leaves at well-spaced points along the stem */}
      <Leaf x={40} y={32} rot={-45} size={1.05} />
      <Leaf x={80} y={78} rot={50} size={1.15} />
      <Leaf x={126} y={130} rot={-40} size={1.0} />

      {/* Two delicate accent leaves on the opposite side of the stem */}
      <Leaf x={28} y={56} rot={120} size={0.75} opacity={0.85} />
      <Leaf x={104} y={108} rot={140} size={0.8} opacity={0.85} />

      {/* Single five-petal bloom near the corner apex */}
      <g transform="translate(18 18)">
        {[0, 72, 144, 216, 288].map((d) => (
          <ellipse
            key={d}
            cx="0"
            cy="-6"
            rx="3"
            ry="5.5"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
            transform={`rotate(${d})`}
          />
        ))}
        <circle r="1.8" fill="currentColor" />
      </g>

      {/* Tiny bud at the far end of the stem */}
      <circle cx="160" cy="168" r="2" fill="currentColor" />
    </svg>
  );
};

/** Flanking sprig — slim horizontal vine ending in a small bloom; mirror with scaleX(-1). */
export const FloralFlank = ({ className = "", width = 110, ...rest }) => (
  <svg
    width={width}
    height={(width * 22) / 110}
    viewBox="0 0 110 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    {...rest}
  >
    <path
      d="M2 11 Q 30 2, 60 11 T 96 11"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      fill="none"
    />
    <path d="M40 6 q 6 -6 12 -4 q -2 7 -12 4 Z" fill="currentColor" opacity="0.85" />
    <path d="M70 16 q 5 6 11 5 q 0 -6 -11 -5 Z" fill="currentColor" opacity="0.85" />
    {/* end bloom */}
    <g transform="translate(100 11)">
      {[0, 90, 180, 270].map((d) => (
        <ellipse
          key={d}
          cx="0"
          cy="-3"
          rx="1.4"
          ry="2.6"
          fill="currentColor"
          opacity="0.9"
          transform={`rotate(${d})`}
        />
      ))}
      <circle r="1.4" fill="currentColor" />
    </g>
  </svg>
);
